import React from "react";
import { motion } from "framer-motion";
import {
  ShieldAlert,
  ShieldCheck,
  Eye,
  Search,
  Activity,
  Navigation as NavIcon,
  MessageCircleHeart,
  ArrowUpRight,
  Compass,
  ArrowRight,
} from "lucide-react";

const capabilities = [
  {
    icon: ShieldAlert,
    title: "Fall Detection",
    desc: "Real‑time monitoring detects falls and abnormal motion patterns, triggering instant alerts with visual context for caregivers.",
  },
  {
    icon: ShieldCheck,
    title: "Fall Prevention",
    desc: "Pattern‑aware AI studies gait, balance, posture and environmental cues to anticipate fall risk and nudge gentle, preventive interventions before an incident occurs.",
  },
  {
    icon: Eye,
    title: "Obstacle Recognition",
    desc: "Vision AI identifies stairs, slippery surfaces and obstacles to prevent accidents before they occur.",
  },
  {
    icon: Search,
    title: "Smart Object Locator",
    desc: "Find misplaced essentials — medication, glasses, phones — through visual cues and item recognition.",
  },
  {
    icon: Activity,
    title: "Behavioral Analysis",
    desc: "Learns daily habits, surfaces anomalies in sleep or movement, and predicts risks through gentle behavioural insights.",
  },
  {
    icon: NavIcon,
    title: "Navigation Assistance",
    desc: "Guided voice and visual cues that support independent movement at home and in assisted outdoor scenarios.",
  },
  {
    icon: MessageCircleHeart,
    title: "Conversational AI Companion",
    desc: "A personalised, voice‑driven companion that assists, reminds and comforts — naturally.",
    size: "wide",
  },
];

export const ElderlyAssist = () => {
  return (
    <section
      id="elderlyassist"
      className="relative py-24 md:py-32 bg-sentivo-bg"
      data-testid="elderlyassist-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* header */}
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="grid lg:grid-cols-12 gap-8 mb-16"
        >
          <div className="lg:col-span-6">
            <div className="inline-flex items-center gap-2 rounded-full bg-sentivo-surface px-4 py-2 text-xs tracking-[0.18em] uppercase text-sentivo-secondary mb-6">
              Flagship Product
            </div>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-sentivo-ink text-balance">
              ElderlyAssist —
              <span className="italic text-sentivo-accent"> an AI guardian </span>
              for independent aging.
            </h2>
          </div>
          <div className="lg:col-span-6 lg:pt-3">
            <p className="text-lg text-sentivo-muted leading-relaxed">
              A context‑aware AI platform that integrates seamlessly into the home —
              combining visual intelligence, sensor data and conversational interfaces
              to deliver personalised, predictive care. Seven core capabilities, paired
              with an advisory practice that enables the broader age‑tech ecosystem.
            </p>
            <a
              href="https://ElderlyAssist.ai"
              target="_blank"
              rel="noreferrer"
              data-testid="elderlyassist-external-link"
              className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-sentivo-primary hover:text-sentivo-accent transition-colors"
            >
              Visit ElderlyAssist.ai
              <ArrowUpRight size={16} />
            </a>
          </div>
        </motion.div>

        {/* bento grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {capabilities.map((c, i) => {
            const Icon = c.icon;
            return (
              <motion.div
                key={c.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.55, delay: i * 0.06 }}
                whileHover={{ y: -4 }}
                className={`group relative overflow-hidden rounded-3xl border border-sentivo-line bg-white p-8 md:p-10 transition-all duration-300 hover:shadow-lg ${
                  c.size === "wide" ? "md:col-span-2 lg:col-span-3" : ""
                }`}
                data-testid={`capability-card-${c.title.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <div
                  className="absolute -top-12 -right-12 w-40 h-40 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                  style={{
                    background: "radial-gradient(circle, rgba(209,108,77,0.15), transparent 70%)",
                  }}
                />
                <div className="relative">
                  <div className="inline-flex items-center justify-center h-12 w-12 rounded-2xl bg-sentivo-surface text-sentivo-accent mb-6 transition-transform duration-300 group-hover:scale-110">
                    <Icon size={22} strokeWidth={1.6} />
                  </div>
                  <h3 className="font-serif text-2xl md:text-3xl text-sentivo-ink leading-tight mb-3">
                    {c.title}
                  </h3>
                  <p className="text-sentivo-muted leading-relaxed">{c.desc}</p>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Featured: Age-Tech Consultancy */}
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7, delay: 0.1 }}
          whileHover={{ y: -4 }}
          className="group relative mt-6 overflow-hidden rounded-3xl bg-[#2C3E35] text-[#FAF9F6] p-8 md:p-12 lg:p-14 transition-shadow hover:shadow-2xl"
          data-testid="capability-card-age-tech-consultancy"
        >
          {/* Animated shimmer sweep */}
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 opacity-30"
            style={{
              background:
                "linear-gradient(110deg, transparent 30%, rgba(209,108,77,0.25) 50%, transparent 70%)",
              backgroundSize: "200% 100%",
              animation: "shimmer 6s linear infinite",
            }}
          />
          {/* Floating orbits */}
          <motion.div
            aria-hidden
            animate={{ scale: [1, 1.08, 1], opacity: [0.25, 0.4, 0.25] }}
            transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
            className="absolute -top-24 -right-24 w-80 h-80 rounded-full"
            style={{
              background:
                "radial-gradient(circle, rgba(209,108,77,0.35) 0%, transparent 65%)",
            }}
          />
          <motion.div
            aria-hidden
            animate={{ scale: [1, 1.12, 1], opacity: [0.15, 0.3, 0.15] }}
            transition={{ duration: 10, repeat: Infinity, ease: "easeInOut", delay: 1 }}
            className="absolute -bottom-20 -left-20 w-72 h-72 rounded-full"
            style={{
              background:
                "radial-gradient(circle, rgba(226,167,142,0.3) 0%, transparent 65%)",
            }}
          />

          <div className="relative grid lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 rounded-full bg-white/10 backdrop-blur px-4 py-2 text-[10px] tracking-[0.22em] uppercase text-[#E2A78E] mb-6 border border-white/10">
                <span className="h-1.5 w-1.5 rounded-full bg-[#D16C4D] animate-pulse" />
                Beyond the product · Advisory
              </div>
              <h3 className="font-serif text-3xl md:text-4xl lg:text-5xl leading-[1.1] tracking-tight text-balance">
                Age‑Tech Consultancy
                <span className="block italic text-[#E2A78E]">to enable the ecosystem.</span>
              </h3>
              <p className="mt-6 max-w-2xl text-base md:text-lg text-[#E6EBE6]/85 leading-relaxed">
                Beyond our own products, SentivoTech advises operators, startups, payors
                and care providers on the strategy, architecture and AI capabilities that
                bring age‑tech to life. We help partners design proactive care models,
                integrate sensing and conversational AI, navigate regulation, and ship
                products that respect dignity at scale.
              </p>

              <div className="mt-8 grid sm:grid-cols-3 gap-5">
                {[
                  { k: "Strategy", v: "Care model & roadmap" },
                  { k: "Architecture", v: "AI, vision & sensor stacks" },
                  { k: "Partnerships", v: "Clinical & ecosystem play" },
                ].map((p) => (
                  <div key={p.k} className="border-l border-white/20 pl-4">
                    <div className="font-serif text-lg text-white">{p.k}</div>
                    <div className="text-xs uppercase tracking-widest text-[#D5DCD5]/70 mt-1">
                      {p.v}
                    </div>
                  </div>
                ))}
              </div>

              <a
                href="#contact"
                data-testid="consultancy-cta"
                className="group/cta mt-10 inline-flex items-center gap-2 rounded-full bg-[#D16C4D] hover:bg-[#B85A3D] text-white px-7 py-4 text-sm font-semibold tracking-wide transition-all duration-300 shadow-sm hover:shadow-md hover:-translate-y-0.5"
              >
                Engage our advisory
                <ArrowRight
                  size={16}
                  className="transition-transform group-hover/cta:translate-x-1"
                />
              </a>
            </div>

            {/* Right visual mark */}
            <div className="lg:col-span-5 relative">
              <div className="relative aspect-square max-w-sm mx-auto">
                <motion.div
                  aria-hidden
                  animate={{ rotate: 360 }}
                  transition={{ duration: 40, repeat: Infinity, ease: "linear" }}
                  className="absolute inset-0 rounded-full border border-white/15"
                />
                <motion.div
                  aria-hidden
                  animate={{ rotate: -360 }}
                  transition={{ duration: 60, repeat: Infinity, ease: "linear" }}
                  className="absolute inset-8 rounded-full border border-white/10"
                />
                <motion.div
                  aria-hidden
                  animate={{ rotate: 360 }}
                  transition={{ duration: 80, repeat: Infinity, ease: "linear" }}
                  className="absolute inset-16 rounded-full border border-[#D16C4D]/30"
                />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="relative">
                    <motion.div
                      animate={{ y: [0, -6, 0] }}
                      transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                      className="h-20 w-20 rounded-2xl bg-[#D16C4D] flex items-center justify-center shadow-xl"
                    >
                      <Compass size={32} strokeWidth={1.6} className="text-white" />
                    </motion.div>
                  </div>
                </div>
                {/* Orbiting dots */}
                {[0, 1, 2, 3].map((i) => (
                  <motion.span
                    key={i}
                    aria-hidden
                    className="absolute top-1/2 left-1/2 h-2.5 w-2.5 -ml-1.5 -mt-1.5 rounded-full"
                    style={{
                      background: i % 2 === 0 ? "#E2A78E" : "#FAF9F6",
                      transformOrigin: "0 0",
                    }}
                    animate={{ rotate: 360 }}
                    transition={{
                      duration: 12 + i * 4,
                      repeat: Infinity,
                      ease: "linear",
                    }}
                  >
                    <span
                      className="block h-2.5 w-2.5 rounded-full"
                      style={{
                        transform: `translateX(${100 + i * 18}px)`,
                        background: i % 2 === 0 ? "#E2A78E" : "#FAF9F6",
                      }}
                    />
                  </motion.span>
                ))}
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
